import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {
    Backdrop,
    Box,
    Button, Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from "@mui/material";
import HeaderView from "./header";
import {AppDataContext} from "../../../App";
import {Direction, ViewMode} from "../../../data/app/state";
import FilterView from "./filter"
import MapView from "./map"
import ListView from "./list"
import _ from "lodash";
import {DragHandle as DragHandleIcon} from "@mui/icons-material";
import TreeInfoView from "./treeInfo";
import UseApiManager from "../../manager/api";
import axios from "axios";
import dayjs from "dayjs";
import { CircularProgressbar } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"
import ExcelJS from "exceljs"
import UserAdminView from "../user_admin"
import LeftTabView from "../leftTab"

const styles = {
    root: {
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
    },
    mainBox: {
        display: "flex",
        position: "relative",
        flexGrow: 1,
    },
    baseBox: {
        flexGrow: '1',
        display: 'flex',
        flexDirection: 'column',
    },
    contentsBox: {
        display: 'flex',
        flexGrow: '1',
        flexDirection: 'row',
    },
    resizeHandle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#eee',
    },
    exportBox: {
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '320px',
        alignItems: 'center',
        height: '300px',
        gap: '8px',
        borderRadius: '8px',
        boxShadow: '1px 1px 3px #666',
    },
    exportStatus: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#3e3e3e',
    },
    exportCancelButton: {
        width: '100px',
        height: '30px',
        background: '#ff6f6f',
        color: 'white',
    },
}

const ExportStatus = {
    Loading: "データ取得中...",
    Generating : "Excel生成中...",
    Downloading: "Excel出力中...",
}

const MainView = (props) => {

    const { state, setFilterModel, setTreeInfoModel } = useContext(AppDataContext)
    const exportCancelRef = useRef()
    const gridApiRef = useRef()
    const resizeHandlerRef = useRef()
    const containerRef = useRef()
    const [resizing, setResizing] = useState(false)
    const { PostOne, Post } = UseApiManager()
    const [xRatio, setXRatio] = useState(0.5)
    const [yRatio, setYRatio] = useState(0.5)
    const [mapWidth, setMapWidth] = useState()
    const [mapHeight, setMapHeight] = useState()
    const [exportStatus, setExportStatus] = useState()
    const [exportError, setExportError] = useState()
    const [exportProgress, setExportProgress] = useState(0)
    const [showUserAdmin, setShowUserAdmin] = useState(false)
    const resizableLimit = {
        top: 100,
        left: 100,
        right: 100,
        bottom: 100,
    }

    const updateMapSize = useCallback(() => {
        console.log("Update map size!!")

        if (!state.direction || !containerRef.current) { return }
        if (_.size(state.viewMode) === 1) {
            setMapWidth(containerRef.current.clientWidth)
            setMapHeight(containerRef.current.clientHeight)
            return
        }
        if (state.direction === Direction.Vertical) {
            setMapWidth(containerRef.current.clientWidth)
            setMapHeight(`${parseInt(containerRef.current.clientHeight * yRatio)}px`)

        } else {
            setMapHeight(containerRef.current.clientHeight)
            setMapWidth(`${parseInt(containerRef.current.clientWidth * xRatio)}px`)
        }
    }, [xRatio, yRatio, state.direction, state.viewMode])

    useEffect(() => {
        const resizeListener = window.addEventListener("resize", _.debounce(() => {
        }, 100))

        return () => {
            window.removeEventListener("resize", resizeListener)
        }
    }, [])

    useEffect(() => {
        updateMapSize()
    }, [xRatio, yRatio, state.direction, state.viewMode])

    const onDragStart = useCallback((e) => {
        console.log("[DragStart]", e)
        setResizing(true)
    }, [state.direction])

    const onDragMove = useCallback((e) => {
        if (resizing) {
            const x = e.clientX - containerRef.current.offsetLeft
            const y = e.clientY - containerRef.current.offsetTop
            if (state.direction === Direction.Horizontal) {
                if (resizableLimit.left > x) { return }
                if (containerRef.current.clientWidth - resizableLimit.right < x) { return }
                setXRatio(x / containerRef.current.clientWidth)
            } else {
                if (resizableLimit.top > y) { return }
                if (containerRef.current.clientHeight - resizableLimit.bottom < y) { return }
                setYRatio(y / containerRef.current.clientHeight)
            }
        }
    }, [state.direction, resizing])

    const onDragEnd = (e) => {
        setResizing(false)
    }

    const onListReady = (api) => gridApiRef.current = api

    const onExportExcel = async () => {

        if (!gridApiRef.current) { return }

        exportCancelRef.current = axios.CancelToken.source()
        setExportStatus(ExportStatus.Loading)
        const total = await PostOne("tree/count", state.filterModel, {cancelToken: exportCancelRef.current.token})

        console.log(total)

        let p = 0
        let rows = []

        while((p * 1000) < total) {
            let res = await Post(`tree/export/${p++ * 1000}/1000`, state.filterModel, {cancelToken: exportCancelRef.current.token})
            rows = rows.concat(res)
            setExportProgress( p * 1000 / total)
        }

        console.log(rows)

        setExportStatus(ExportStatus.Generating)

        const excelColumns = state.columnDefs.filter(v => v.field && v.excel).sort((v1, v2) => v1.excel?.sort - v2.excel?.sort)
        console.log(excelColumns)
        setExportProgress(0)

        const wb = new ExcelJS.Workbook()

        const ws = wb.addWorksheet("樹木リスト")

        excelColumns.forEach((col, c) => {
            ws.getCell(1, c + 1).value = col.name
        })
        rows.forEach((row, r) => {
            setExportProgress( r / total)
            excelColumns.forEach((col, c) => {
                const cellType = col.excel.type ?? col.dataType
                let val = row[col.field]
                if (cellType === "date") {
                    val = dayjs(val.replace("Z", "")).format("YYYY-MM-DD HH:mm")
                }
                ws.getCell(r + 2, c + 1).value = val
            })
        })
        setExportStatus(ExportStatus.Downloading)

        wb.xlsx.writeBuffer()
            .then(buff => {
                const blob = new Blob([buff], {type: "application/octet-binary"})
                const a = document.createElement("a")
                a.href = window.URL.createObjectURL(blob)
                a.download = `横浜街路樹-${dayjs().tz().format("YYYY年M月D日")}.xlsx`
                a.click()
                a.remove()
                setExportStatus(null)
                setExportProgress(0)
            })
    }

    const onExportCancel = () => {
        exportCancelRef.current?.cancel()
        setExportStatus(null)
    }

    const onRemoveFilter = (filter, colDef) => {
        const model = {...gridApiRef.current.getFilterModel()}
        console.log(filter, model, colDef)
        delete model[colDef.field]
        setFilterModel(model)
        gridApiRef.current.setFilterModel(model)
    }

    const onClearConditions = () => {
        setFilterModel(null)
        gridApiRef.current.setFilterModel({})
    }

    const onUserAdmin = () => {
        setShowUserAdmin(true)
    }

    return (
        <Box style={{...styles.root, userSelect: resizing ? "none": "auto"}} onMouseUp={onDragEnd} onMouseMove={onDragMove} onMouseLeave={onDragEnd}>
            <HeaderView
                onExportExcel={onExportExcel}
                onClearConditions={onClearConditions}
                onUserAdmin={onUserAdmin}
            />
            <Box style={styles.contentsBox}>
                <LeftTabView tabMode={state.tabMode} />
                <Box style={styles.baseBox}>
                    <FilterView onRemoveFilter={onRemoveFilter} />
                    <Box ref={containerRef} style={{...styles.mainBox, flexDirection: state.direction === Direction.Horizontal ? "row": "column"}}>
                        {state.viewMode.includes(ViewMode.Map) && (<MapView width={mapWidth} height={mapHeight} />)}
                        {_.size(state.viewMode) > 1 && (<Box
                            ref={resizeHandlerRef}
                            style={{...styles.resizeHandle, cursor: resizing ? "grabbing" : "grab"}}
                            onMouseDown={onDragStart}
                        >
                            <DragHandleIcon style={{transform: state.direction === Direction.Horizontal ? "rotate(90deg)" : ""}} />
                        </Box>)}
                        {state.viewMode.includes(ViewMode.List) && <ListView onReady={onListReady} />}
                    </Box>
                </Box>
            </Box>
            <TreeInfoView onClose={() => setTreeInfoModel(null)} />
            <Backdrop open={!!exportStatus}>
                <Box style={styles.exportBox}>
                    <Typography style={styles.exportStatus}>{exportStatus}</Typography>
                    <Box style={{height: "100px", width: "100px"}}>
                        <CircularProgressbar value={exportProgress} maxValue={1} text={`${parseInt(exportProgress * 100)+1}%`} />
                    </Box>
                    <Button style={styles.exportCancelButton} disabled={exportStatus !== ExportStatus.Generating} onClick={onExportCancel}>キャンセル</Button>
                </Box>
            </Backdrop>
            <Backdrop open={showUserAdmin} style={{zIndex: 2}}>
                <UserAdminView onClose={() => setShowUserAdmin(false)} />
            </Backdrop>
            <Dialog open={!!exportError}>
                <DialogTitle>出力に失敗しました</DialogTitle>
                <DialogContent>
                    <DialogContentText>生成中にエラーが発生しました</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportError(null)}>OK</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )

}

export default MainView
